import { format, subYears } from "date-fns";
import type { NestedDeal } from "@revelate/types";
import type { ForecastPresentationDeal, ForecastPresentationHistoryDeal, ForecastPresentationSnapshot, ForecastPresentationScope } from "@revelate/types/forecast-presentation";
import { applyExpectedCloseV2Forecast } from "../expected-close-v2/localExpectedCloseForecast";
import { getExpectedCloseV2Timing } from "../forecast-diagnosis/forecastDiagnosisContract";
import { buildRiskHistoryDealSnapshot } from "../lib/risk-history";
import { getStageLabel, getStagePipelineId, getStageProbability } from "../lib/pipelines";
import { compareForecastSnapshots } from "./movement";
import { matchesForecastScope, projectFuturePipeline, snapshotPeriodValue } from "./projection";
import type { ForecastRunHistory } from "./forecastRunHistory";
import { recoverFutureHistoryDates } from "./futureHistoryRecovery";

type ParsedDeal = NestedDeal & { provider_data: ReturnType<typeof JSON.parse> };

function historicalState(raw: ParsedDeal, asOf: Date, scored: boolean, pipelines: unknown[]) {
  const restored = buildRiskHistoryDealSnapshot(raw, asOf);
  const history = restored.provider_data?.propertiesWithHistory ?? {};
  const bool = (value: unknown) => value === true || value === "true" || value === false || value === "false";
  const flagsKnown = ["hs_is_closed_won", "hs_is_closed_lost"].every(key => bool(history[key]?.[0]?.value));
  const observedBefore = Boolean(raw.updated_at && Number.isFinite(new Date(raw.updated_at).getTime()) && new Date(raw.updated_at) <= asOf);
  if (scored) return { restored, known: true, isWon: false, isLost: false, isClosed: false };
  if (flagsKnown) return { restored, known: true, isWon: restored.is_won, isLost: restored.is_lost, isClosed: restored.is_closed };
  if (observedBefore) return { restored: { ...restored, closed_at: raw.closed_at }, known: true, isWon: Boolean(raw.is_won), isLost: Boolean(raw.is_lost), isClosed: Boolean(raw.is_closed) };
  const stageId = history.dealstage?.[0]?.value;
  const pipelineId = typeof stageId === "string" ? getStagePipelineId(pipelines, stageId) : null;
  const pipeline = pipelines.find(item => (item as { id: string }).id === pipelineId) as { stages: Array<{ id: string; metadata?: { probability?: unknown } }> } | undefined;
  const stage = pipeline?.stages.find(item => item.id === stageId);
  if (stage && stage.metadata?.probability !== undefined && Number.isFinite(Number(stage.metadata.probability))
    && Number(stage.metadata.probability) >= 0 && Number(stage.metadata.probability) <= 1) {
    const probability = getStageProbability(pipelines, pipelineId, stageId);
    if (probability === Number(stage.metadata.probability)) {
      return { restored, known: true, isWon: probability === 1, isLost: probability === 0, isClosed: probability === 0 || probability === 1 };
    }
  }
  return { restored, known: false, isWon: false, isLost: false, isClosed: false };
}

/** Approved reference: today's canonical outcome minus only verifiable changes. */
export function buildReconstructedForecastMovement(input: {
  current: ForecastPresentationSnapshot; previous: ForecastRunHistory; rawDeals: NestedDeal[];
  currentOutcome: number; currentWon: number; currentFuture: number | null; months: ReadonlySet<string>; scope: ForecastPresentationScope;
  range: { start: Date; end: Date }; pipelines: unknown[];
}) {
  const { current, previous, months, scope, range } = input;
  if (current.companyId !== previous.artifact.companyId || current.modelVersion !== previous.modelVersion
    || !(new Date(previous.asOf) < new Date(current.capturedAt)) || !Number.isFinite(input.currentOutcome)) {
    return { available: false as const, reason: "A comparable earlier forecast is required." };
  }
  // The canonical chart rounds each actual win before summing. Consume that
  // already-calculated Won total, rather than duplicating its rounding here.
  const projectedOpen = snapshotPeriodValue({ ...current, deals: current.deals.filter(deal => !deal.isWon) }, months, scope);
  if (Math.abs(projectedOpen + input.currentWon - (input.currentOutcome - (input.currentFuture ?? 0))) > .51) {
    return { available: false as const, reason: "Deal contributions are still updating. Refresh before comparing." };
  }
  const asOf = new Date(previous.asOf);
  const priorById = new Map(previous.artifact.dealForecasts.map(deal => [deal.dealId, deal]));
  const uncertainBuyerIds = new Set(previous.uncertainBuyerOutcomeIds);
  const priorTerminalIds = new Set(previous.artifact.terminalBuyerOutcomeDeals?.map(deal => deal.dealId));
  const rawById = new Map(input.rawDeals.map(deal => [Number(deal.id), { ...deal,
    provider_data: typeof deal.provider_data === "string" ? JSON.parse(deal.provider_data) : deal.provider_data } as ParsedDeal]));
  const currentById = new Map(current.deals.map(deal => [deal.id, deal]));
  const before: ForecastPresentationDeal[] = [];
  const after: ForecastPresentationDeal[] = [];
  const history: ForecastPresentationHistoryDeal[] = [];
  const historicalRaw: Array<{ id: number; provider_data: unknown }> = [];
  let unknownFutureInputs = 0;
  let unverifiedDeals = 0;
  for (const member of current.history) {
    const raw = rawById.get(member.id);
    if (!raw) continue;
    const forecast = priorById.get(member.id);
    const created = member.createdAt ? new Date(member.createdAt) : null;
    const selected = matchesForecastScope(member, scope);
    const state = historicalState(raw, asOf, Boolean(forecast), input.pipelines);
    const today = currentById.get(member.id) ?? { ...member, name: raw.name ?? "Deal", ownerName: "Owner not assigned", stage: null,
      isClosed: Boolean(raw.is_closed), source: "reviewed-fallback" as const, expectedClose: null, monthlyContribution: [] };
    const existed = created && Number.isFinite(created.getTime()) && created <= asOf;
    if (selected && (!created || !Number.isFinite(created.getTime()))) unknownFutureInputs++;
    if (existed) {
      historicalRaw.push({ id: member.id, provider_data: state.restored.provider_data });
      const close = state.restored.closed_at ? new Date(state.restored.closed_at) : null;
      const validClose = close && Number.isFinite(close.getTime());
      if (selected && (!state.known || (state.isWon && !validClose)) && created >= subYears(asOf, 2)) unknownFutureInputs++;
      history.push({ ...member, isWon: state.isWon, isLost: state.isLost, closedAt: validClose ? close.toISOString() : null });
    }
    if (uncertainBuyerIds.has(member.id)) { unverifiedDeals++; continue; }
    if (created && created > asOf && !forecast) {
      // A genuinely new Deal has a known zero contribution at the old date,
      // even when its current contribution uses the unchanged per-Deal fallback.
      after.push(today);
      continue;
    }
    if (!existed && !forecast) { unverifiedDeals++; continue; }
    if (forecast) {
      const rows = forecast.wonProbabilityByMonth.map(([month]) => ({ month, won: 0, total_minus_risk: 0 }));
      const contributions = applyExpectedCloseV2Forecast(rows, previous.artifact, [today], false)
        .map(row => [row.month, Number(row.expected_close_v2)] as [string, number]);
      const timing = getExpectedCloseV2Timing(forecast, months, format(range.end, "yyyy-MM"));
      before.push({ ...today, isWon: false, isLost: false, isClosed: false, source: priorTerminalIds.has(member.id) ? "terminal" : "v2",
        monthlyContribution: contributions, expectedClose: timing?.expectedCloseMonth ? `${timing.expectedCloseMonth}-01T12:00:00` : null,
        stage: getStageLabel(input.pipelines, state.restored.pipeline, state.restored.provider_data?.properties?.dealstage) || null });
      after.push(today);
    } else if (state.known && state.isClosed && (!state.isWon || state.restored.closed_at && Number.isFinite(new Date(state.restored.closed_at).getTime()))) {
      before.push({ ...today, isWon: state.isWon, isLost: state.isLost, isClosed: true, source: "actual",
        closedAt: state.restored.closed_at ? new Date(state.restored.closed_at).toISOString() : null,
        monthlyContribution: state.isWon ? [[format(new Date(state.restored.closed_at), "yyyy-MM"), today.value]] : [] });
      after.push(today);
    } else {
      // No saved V2 contribution for this pre-existing Deal: do not invent a
      // historical fallback, or silently call new model coverage sales progress.
      unverifiedDeals++;
    }
  }
  const oldProjection: ForecastPresentationSnapshot = { ...current, runId: previous.runId, asOf: previous.asOf,
    capturedAt: previous.asOf, deals: before, history: [] };
  const movement = compareForecastSnapshots(oldProjection, { ...current, deals: after, history: [] }, months, scope, range);
  if (!movement.available) return movement;
  const oldFuture = unknownFutureInputs === 0 ? projectFuturePipeline(history, asOf, range, scope,
    { boundedHistory: true, recoveredById: recoverFutureHistoryDates(history, historicalRaw, asOf) }).future.value : null;
  const includesFuture = oldFuture !== null && input.currentFuture !== null;
  const futureDelta = includesFuture ? input.currentFuture! - oldFuture! : 0;
  const otherVerifiedChanges = movement.dealsByCategory.unclassified.reduce((sum, deal) => sum + deal.forecastImpact, 0);
  const netChange = Object.values(movement.values).reduce((sum, value) => sum + value, 0) + otherVerifiedChanges + futureDelta;
  const snapshotForecast = input.currentOutcome - netChange;
  if (!Number.isFinite(snapshotForecast)) return { available: false as const, reason: "The comparison amounts are unavailable." };
  for (const deals of Object.values(movement.dealsByCategory)) {
    deals.sort((a, b) => Math.abs(b.forecastImpact) - Math.abs(a.forecastImpact));
    for (const deal of deals) deal.changeSummary = "Change in the selected-period contribution, using today's Deal amount at both dates.";
  }
  return { ...movement, snapshotForecast, currentForecast: input.currentOutcome, futureDelta, includesFuture, reconstructed: true,
    previousDate: `${format(asOf, "d MMM")} · reconstructed`,
    coverage: { missingCurrentRecords: previous.artifact.dealForecasts.filter(deal => !rawById.has(deal.dealId)).length,
      unverifiedDeals, comparedDeals: after.filter(deal => matchesForecastScope(deal, scope)).length, unknownFutureInputs } };
}
